import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { PaymentService } from "../../services/payment.service";
import { AddNewCardComponent } from "../add-new-card/add-new-card.component";
import { Helper } from "src/app/core/helpers/helper";

@Component({
  selector: "app-booking-payment-modal",
  templateUrl: "./booking-payment-modal.component.html",
  styleUrl: "./booking-payment-modal.component.scss",
  standalone: false
})
export class BookingPaymentModalComponent {
  selectedCard: any;
  paymentGateways: any;
  paymentGatewaysList: any;
  selectedGatewayType: any;
  isLaterStripePaymentThenHideOtherMethod: boolean = false;
  isPaymentOptionError: boolean = false;

  cards: Array<any> = [];

  amount!: number;

  bookingPaymentModalRef!: NgbModalRef;

  @Input() currentView!: number;
  @Output() paymentEvent = new EventEmitter<any>();
  @Output() selectPaymentEmmiter = new EventEmitter<any>();

  @ViewChild("bookingPaymentModal", { static: true }) bookingPaymentModal!: TemplateRef<any>;
  @ViewChild("addNewCard", { static: true }) addNewCard!: AddNewCardComponent;

  constructor(public modalService: NgbModal, public helper: Helper, private paymentService: PaymentService) {}

  /**
   * Opens the booking payment modal and initializes payment settings.
   * @param amount The amount to be paid.
   * @param selectedPaymentMenthod The selected payment method.
   * @param cityPaymentSetting The city-specific payment settings.
   * @param hideOtherMethod Flag to hide other payment methods.
   * @returns {void}
   */
  openModal(amount: number, selectedPaymentMenthod: any, cityPaymentSetting: any, hideOtherMethod: boolean = false): void {
    this.amount = amount;
    this.isLaterStripePaymentThenHideOtherMethod = hideOtherMethod;
    this.selectedGatewayType = selectedPaymentMenthod ? selectedPaymentMenthod.key : null;
    this.openBookingPaymentModal();
    this.getCitySettingPaymentList(cityPaymentSetting);
    this.cards = [];
  }

  /**
   * Opens the booking payment modal.
   * @returns {void}
   */
  openBookingPaymentModal(): void {
    this.bookingPaymentModalRef = this.modalService.open(this.bookingPaymentModal, { centered: true, backdrop: "static" });
  }

  /**
   * Closes the booking payment modal.
   * @returns {void}
   */
  closeBookingPaymentModal(): void {
    this.bookingPaymentModalRef?.close();
    setTimeout(() => {
      this.selectedGatewayType = null;
    }, 400);
  }

  /**
   * Handles the change event for the payment gateway radio buttons.
   * @param gateway The selected payment gateway.
   * @returns {void}
   */
  onRadioChange(gateway: any): void {
    this.selectedGatewayType = gateway.key;
    this.getCardList(this.selectedGatewayType);
    const isStripeOrPaystack = this.helper.PaymentGatewayCardEnumArray.includes(this.selectedGatewayType);
    if (isStripeOrPaystack) {
      this.isPaymentOptionError = !this.cards.some((card) => card.paymentGatewayType === this.selectedGatewayType);
    }
  }

  /**
   * Retrieves the list of payment gateways based on city settings.
   * @param cityPaymentSetting The city-specific payment settings.
   * @returns {void}
   */
  getCitySettingPaymentList(cityPaymentSetting: any): void {
    if (cityPaymentSetting) {
      this.paymentGateways = this.helper.PaymentGateway.filter((x: any) => cityPaymentSetting.paymentGateways.includes(x.key));
      const capturePaymentIntent = localStorage.getItem("capturePaymentIntent");
      if (!capturePaymentIntent) {
        if (cityPaymentSetting.isWallet) {
          this.paymentGateways.splice(0, 0, { key: this.helper.PaymentMode.Wallet, string: this.helper.PaymentModeStrings[this.helper.PaymentMode.Wallet] });
        }
        if (cityPaymentSetting.isCash) {
          this.paymentGateways.splice(0, 0, { key: this.helper.PaymentMode.Cash, string: this.helper.PaymentModeStrings[this.helper.PaymentMode.Cash] });
        }
      }
      if (this.helper.PaymentGatewayCardEnumArray.includes(this.selectedGatewayType)) {
        this.getCardList(this.selectedGatewayType);
      }
      this.paymentGatewayList();
    }
  }

  /**
   * Retrieves the list of payment gateways.
   * @returns {void}
   */
  paymentGatewayList(): void {
    this.paymentService.getPaymentGetway({ countryId: localStorage.getItem("countryId") }).subscribe((response: any) => {
      if (response) {
        this.paymentGatewaysList = response?.paymentGateways;
        this.paymentGateways = this.paymentGateways.filter(
          (x: any) =>
            x.key !== this.helper.PaymentMode.Cash && x.key !== this.helper.PaymentMode.Wallet // Exclude Cash (key: 1) and Wallet (key: 2)
              ? this.paymentGatewaysList.some((element: any) => element.type === x.key)
              : true // Include Cash and Wallet without checking
        );
        if (this.selectedGatewayType === this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Stripe]) {
          const paytmentGateway = this.paymentGatewaysList.filter((x: any) => x.type === this.selectedGatewayType);
          this.helper.loadStripe(paytmentGateway[0]?.credential?.publicKey);
        }
      }
    });
  }

  /**
   * Retrieves the list of cards for the selected payment gateway.
   * @param paymentGatewayTypes The selected payment gateway types.
   * @returns {void}
   */
  getCardList(paymentGatewayTypes: any): void {
    const params = { paymentGatewayTypes: paymentGatewayTypes.toString(), isShowErrorToast: false };
    this.cards = [];
    if (!this.helper.PaymentGatewayCardEnumArray.includes(this.selectedGatewayType)) {
      return;
    }
    this.paymentService.getCardList(params).subscribe((res: any) => {
      if (res) {
        this.cards = res.cards;
        this.selectedCard = this.cards.find((card: any) => card.isDefault);
        this.isPaymentOptionError = !this.cards.some((card) => card.paymentGatewayType === this.selectedGatewayType);
      }
    });
  }

  /**
   * Handles the selection of a card.
   * @param card The selected card.
   * @returns {void}
   */
  onSelectCard(card: any): void {
    this.selectedCard.isDefault = false;
    this.selectedCard = card;
    card.isDefault = true;
    this.paymentService.selectCard(card._id).subscribe();
  }

  /**
   * Changes the selected payment gateway.
   * @returns {void}
   */
  selectGateway(): void {
    this.getCardList(this.selectedGatewayType);
  }

  /**
   * Adds a new card from the selected payment gateway.
   * Opens the modal for adding a new card if the gateway type is supported.
   * @returns {void}
   */
  addCard(): void {
    const paytmentGateway = this.paymentGatewaysList.filter((x: any) => x.type === this.selectedGatewayType);
    const params = { countryId: this.helper.adminSettings.countryId, isShowSuccessToast: false };
    this.paymentService.addCard(this.selectedGatewayType, params).subscribe((response: any) => {
      if (response.success && response.gatewaytype === this.helper.PaymentGatewayType[this.helper.PaymentGatewayEnum.Stripe]) {
        // Open modal for Stripe
        this.addNewCard.openModal(paytmentGateway[0], response.data.intent);
      }
    });
  }

  /**
   * Applies the selected payment method and closes the modal.
   * @returns {void}
   */
  applyPaymentMethod(): void {
    if (!this.selectedGatewayType) {
      return;
    }
    this.selectPaymentEmmiter.emit(this.selectedGatewayType);
    this.closeBookingPaymentModal();
  }
}
